import * as React from 'react';
import { useApiGateway } from 'hooks/useApi';
import type { FlowAnalysisResponse } from 'constants/payloads/flowAnalysis';
import { flowAnalysisTimestamp } from 'layouts/FlowPageContainer/flowAiAnalysisSchedule';

// Publication time of the last report this browser opened. A new key: the old
// `flow-ai-analysis-last-checked` was written by every background poll, even for visitors
// who never opened the analysis, so it cannot mean "read".
export const LAST_READ_STORAGE_KEY = 'flow-ai-analysis-last-read';
export const FLOW_ANALYSIS_POLL_MS = 6 * 60_000;
export const FLOW_ANALYSIS_REQUEST_TIMEOUT_MS = 20_000;
// Far beyond any device clock error. A later read time would hide every real report's badge.
const MAX_READ_AHEAD_MS = 24 * 60 * 60_000;

const validTimestamp = (value: string | null): string | null =>
  value !== null && Number.isFinite(Date.parse(value)) ? value : null;

/**
 * Fetching a new report never blanks the report already being read.
 *
 * New badge: shown while the latest known report is newer than the last one this browser
 * opened, and kept until the reader opens it. A browser that has never opened the analysis
 * has nothing to compare against, so it gets no badge and makes no background requests.
 */
export default function useFlowAiAnalysis(isPageLoading: boolean, isOpen = false) {
  const [analysis, setAnalysis] = React.useState<FlowAnalysisResponse | null>(null);
  const [isRefreshing, setIsRefreshing] = React.useState(false);
  const [isError, setIsError] = React.useState(false);
  // Server publication time of the newest received report, for read tracking only. Unlike the
  // displayed time, it is not checked against this device's clock, so a slow clock cannot make
  // an already-read report look new once its displayed time becomes valid.
  const [publishedAt, setPublishedAt] = React.useState<string | null>(null);
  const [lastRead, setLastRead] = React.useState<string | null>(null);
  const { get, stopRequest } = useApiGateway<never, FlowAnalysisResponse>('/flow/ai-analysis');
  const getRef = React.useRef(get);
  getRef.current = get;
  const stopRef = React.useRef(stopRequest);
  stopRef.current = stopRequest;
  const generation = React.useRef(0);
  const cancelRequest = React.useRef<(() => void) | null>(null);
  const mounted = React.useRef(false);
  const inFlight = React.useRef<Promise<void> | null>(null);
  const latestReport = React.useRef<FlowAnalysisResponse | null>(null);
  const lastReadRef = React.useRef<string | null>(null);
  const lastAttemptAt = React.useRef<number | null>(null);

  /** Only moves forward, so an older tab or response cannot re-flag a report as new. */
  const recordRead = React.useCallback((timestamp: string, persist: boolean) => {
    const previous = lastReadRef.current;
    if (Date.parse(timestamp) > Date.now() + MAX_READ_AHEAD_MS) return;
    if (previous && Date.parse(timestamp) <= Date.parse(previous)) return;
    lastReadRef.current = timestamp;
    setLastRead(timestamp);
    if (!persist) return;
    // Storage may be disabled. That must never prevent someone reading their report.
    try {
      localStorage.setItem(LAST_READ_STORAGE_KEY, timestamp);
    } catch {}
  }, []);

  React.useEffect(() => {
    mounted.current = true;
    try {
      const stored = validTimestamp(localStorage.getItem(LAST_READ_STORAGE_KEY));
      if (stored) recordRead(stored, false);
    } catch {}
    // Opening the analysis in another tab clears this tab's badge too.
    const onStorage = (event: StorageEvent) => {
      const stored = event.key === LAST_READ_STORAGE_KEY ? validTimestamp(event.newValue) : null;
      if (stored) recordRead(stored, false);
    };
    window.addEventListener('storage', onStorage);
    return () => {
      window.removeEventListener('storage', onStorage);
      mounted.current = false;
      generation.current += 1;
      inFlight.current = null;
      cancelRequest.current?.();
      cancelRequest.current = null;
      stopRequest();
    };
  }, [stopRequest, recordRead]);

  const refresh = React.useCallback((): Promise<void> => {
    // Opening, a poll and a retry can coincide. One request serves all three.
    if (inFlight.current) return inFlight.current;
    const requestGeneration = ++generation.current;
    lastAttemptAt.current = Date.now();
    setIsRefreshing(true);
    setIsError(false);
    const request = (async () => {
      let deadline: ReturnType<typeof setTimeout> | null = null;
      try {
        const timeout = new Promise<never>((_resolve, reject) => {
          cancelRequest.current = () => reject(new Error('Analysis update cancelled'));
          deadline = setTimeout(() => {
            if (requestGeneration === generation.current) stopRef.current();
            reject(new Error('Analysis update timed out'));
          }, FLOW_ANALYSIS_REQUEST_TIMEOUT_MS);
        });
        // A hanging token lookup or fetch must not disable all future open/poll/retry attempts.
        // Once the race settles, an old response can no longer commit over a successful retry.
        const response = await Promise.race([
          Promise.resolve().then(() => getRef.current()),
          timeout,
        ]);
        if (!mounted.current || requestGeneration !== generation.current) return;
        const result = response.data;
        if (response.isError || !result || typeof result === 'string' || !result.markdown?.trim()) {
          setIsError(true);
          return;
        }
        const timestamp =
          typeof result.lastUpdated === 'string' &&
          flowAnalysisTimestamp(result.lastUpdated, Date.now()) !== null
            ? result.lastUpdated
            : null;
        const previous = latestReport.current?.lastUpdated;
        // An older cached response must not roll a newer, already displayed report backwards.
        if (timestamp && previous && Date.parse(timestamp) < Date.parse(previous)) return;
        // An older API answer has no report field; the modal then renders the markdown.
        const next = {
          markdown: result.markdown,
          lastUpdated: timestamp,
          report: result.report ?? null,
        };
        latestReport.current = next;
        setAnalysis(next);
        setPublishedAt(
          typeof result.lastUpdated === 'string' ? validTimestamp(result.lastUpdated) : null,
        );
      } catch {
        if (mounted.current && requestGeneration === generation.current) setIsError(true);
      } finally {
        if (deadline !== null) clearTimeout(deadline);
        if (requestGeneration === generation.current) {
          cancelRequest.current = null;
          inFlight.current = null;
          if (mounted.current) setIsRefreshing(false);
        }
      }
    })();
    inFlight.current = request;
    return request;
  }, []);

  // Whatever the open modal shows has been read, including a report that arrives while it is open.
  React.useEffect(() => {
    if (isOpen && publishedAt) recordRead(publishedAt, true);
  }, [isOpen, publishedAt, recordRead]);

  const tracksNewReports = lastRead !== null;
  React.useEffect(() => {
    if (isPageLoading || !tracksNewReports) return;
    // Check on arrival and when a hidden tab returns, unless a poll-interval's worth of
    // requests already ran. Hidden tabs skip polls, so this is the catch-up, not extra load.
    const checkIfStale = () => {
      if (document.visibilityState === 'hidden') return;
      const last = lastAttemptAt.current;
      if (last !== null && Date.now() - last < FLOW_ANALYSIS_POLL_MS) return;
      void refresh();
    };
    checkIfStale();
    const interval = setInterval(() => {
      if (document.visibilityState !== 'hidden') void refresh();
    }, FLOW_ANALYSIS_POLL_MS);
    const onVisibility = () => {
      if (document.visibilityState === 'visible') checkIfStale();
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [isPageLoading, tracksNewReports, refresh]);

  const showNewBadge =
    !isOpen &&
    publishedAt !== null &&
    lastRead !== null &&
    Date.parse(publishedAt) > Date.parse(lastRead);

  return { analysis, isRefreshing, isError, showNewBadge, refresh };
}
